/**
 * Mirrors `@zhichetech/models` apdm/build-plan, build-cards, build-launch
 * and build-widgets: the guided build of a campaign in the 知车 AI chat
 * (spec §11.4).
 */
import {
  ApdmCampaignDailyWindow,
  ApdmCampaignDefinition,
  ApdmCampaignKind,
  ApdmCampaignRecipientFacts,
  ApdmCampaignStatus,
  parseApdmDailyWindow,
} from './campaign';
import { ApdmAudienceCandidate } from './campaign-api';
import { ApdmCouponCampaignSummary } from './campaign-coupon';
import {
  ApdmCampaignPreviewCounts,
  ApdmCampaignPreviewResult,
  ApdmContactBlockReason,
} from './campaign-preview';

export enum ApdmBuildStep {
  What = 'what',
  Who = 'who',
  When = 'when',
  Which = 'which',
  Messages = 'messages',
  Launch = 'launch',
}

export const kApdmBuildSteps: readonly ApdmBuildStep[] = [
  ApdmBuildStep.What,
  ApdmBuildStep.Who,
  ApdmBuildStep.When,
  ApdmBuildStep.Which,
  ApdmBuildStep.Messages,
  ApdmBuildStep.Launch,
];

export enum ApdmPlanFactState {
  Known = 'known',
  Inferred = 'inferred',
  Missing = 'missing',
}

export interface ApdmBuildChecklistStep {
  step: ApdmBuildStep;
  confirmed: boolean;
  facts: { fact: string; state: ApdmPlanFactState }[];
}

export interface ApdmBuildChecklist {
  steps: ApdmBuildChecklistStep[];
  currentStep: ApdmBuildStep | null;
  launchedCampaignId: string | null;
  /** The offer the AI inferred: the coupon choice card preselects it. */
  suggestedOffer:
    | { kind: 'none' }
    | { kind: 'coupon_campaign'; couponCampaignId: string }
    | null;
}

/** What the build has settled so far, for the chat header's chips. */
export interface ApdmPlanSettledFacts {
  kind: ApdmCampaignKind | null;
  audienceCount: number | null;
}

/** The campaign a conversation launched, live. */
export interface ApdmCampaignMonitor {
  campaignId: string;
  title: string;
  status: ApdmCampaignStatus;
  audience: number;
  /** Tried so far: reached, unconfirmed or unreachable. */
  attempted: number;
  reached: number;
  unreachable: number;
  /** Asked not to be contacted. */
  declined: number;
  replied: number;
  leads: number;
  /**
   * When its sending next opens: now while today's window is open, else
   * the next window on or after its start. Null once paused or ended, or
   * when no window is left before its end.
   */
  nextSendAt: string | null;
  /** Messages handed to the sending accounts that have not gone out yet. */
  queued: number;
  /** Sending accounts online now: none means nothing goes out. */
  liveSendingAccounts: number;
}

export interface ApdmSessionChecklistResult {
  checklist: ApdmBuildChecklist | null;
  settled: ApdmPlanSettledFacts | null;
  campaign: ApdmCampaignMonitor | null;
}

/**
 * The days a campaign runs, in the business timezone: from the start of
 * `startDate` to the end of `endDate`. The daily window decides the hours
 * it sends on each of them.
 */
export interface ApdmPlanPeriod {
  /** `YYYY-MM-DD` */
  startDate: string;
  /** `YYYY-MM-DD`, the last day it sends on; null for no end. */
  endDate: string | null;
  /** Customers messaged a day: absent keeps the kind's default, null has
   * no limit. */
  dailyCap?: number | null;
  /** The hours it sends in each day: absent keeps the kind's default. */
  dailyWindow?: ApdmCampaignDailyWindow;
}

/** A tapped answer: the user's own choice, recorded with their message. */
export type ApdmStepAnswer =
  | { step: ApdmBuildStep.What; kind: ApdmCampaignKind }
  | { step: ApdmBuildStep.Who }
  /**
   * A sheet uploaded in the audience step finished importing: not the
   * user's answer, so it shows no bubble and settles nothing.
   */
  | { step: ApdmBuildStep.Who; importId: string }
  /** The campaign was launched, paused, resumed or stopped from a card. */
  | {
      step: ApdmBuildStep.Launch;
      campaignId: string;
      outcome: ApdmCampaignOutcome;
    }
  | { step: ApdmBuildStep.When; period: ApdmPlanPeriod }
  | {
      step: ApdmBuildStep.Which;
      offer:
        | { kind: 'none' }
        | { kind: 'coupon_campaign'; couponCampaignId: string };
    }
  /** Accepts the offer the plan holds: the composed coupon campaign. */
  | { step: ApdmBuildStep.Which }
  /** The messages card accepted, with the texts as the user left them. */
  | {
      step: ApdmBuildStep.Messages;
      variants: { text: string; applicability?: string | null }[];
    };

export enum ApdmCampaignOutcome {
  Launched = 'launched',
  Paused = 'paused',
  Resumed = 'resumed',
  Stopped = 'stopped',
}

export enum ApdmCampaignControl {
  Pause = 'pause',
  Resume = 'resume',
  Stop = 'stop',
}

export enum ApdmBuildCardKind {
  Audience = 'audience',
  CouponChoice = 'coupon_choice',
  Messages = 'messages',
  Campaign = 'campaign',
  Upload = 'upload',
}

/** Why the who step waits for the user's customer sheet. */
export enum ApdmPlanUploadReason {
  OwnSheet = 'own_sheet',
  NoCustomerData = 'no_customer_data',
}

/** The who step waiting for a customer sheet: the way to upload it. */
export interface ApdmPlanUploadCard {
  asOf: string;
  kind: ApdmCampaignKind;
  reason: ApdmPlanUploadReason;
}

export interface ApdmPlanAudienceCard {
  kind: ApdmCampaignKind;
  audience: ApdmCampaignDefinition['audience'];
  asOf: string;
  counts: ApdmCampaignPreviewCounts;
  samples: ApdmAudienceCandidate[];
}

/** One customer an audience card counted, as its full list shows them. */
export interface ApdmPlanAudienceListEntry {
  licensePlateNo: string;
  facts: Pick<
    ApdmCampaignRecipientFacts,
    'customerName' | 'vehicleBrandModel' | 'lastVisitAt'
  >;
  knownReachable: boolean;
  /** Why the campaign would not message them; empty when it would. */
  excludedBy: ApdmContactBlockReason[];
}

/** A page of an audience card's customers, in plate order. */
export interface ApdmPlanAudienceListPage {
  customers: ApdmPlanAudienceListEntry[];
  /** Where the next page starts; null after the last. */
  nextAfterPlate: string | null;
}

export interface ApdmPlanCouponChoiceCard {
  asOf: string;
  choices: ApdmCouponCampaignSummary[];
  /** A coupon campaign composed for the plan, created at launch; first. */
  composed?: {
    proposal: {
      coupon: { name: string; title?: string; items: { label: string }[] };
    };
    startsAt: string;
    endsAt: string;
  } | null;
}

export interface ApdmPlanMessagesCard {
  asOf: string;
  title: string;
  variants: { text: string; applicability?: string | null }[];
}

export interface ApdmPlanCampaignCard
  extends Omit<ApdmCampaignPreviewResult, 'campaign'> {
  definition: ApdmCampaignDefinition;
  /** A coupon campaign composed in the build; created at launch. */
  composedCoupon: {
    coupon: { name: string; title?: string; items: { label: string }[] };
  } | null;
}

export type ApdmBuildCardView =
  | { card: ApdmBuildCardKind.Audience; audience: ApdmPlanAudienceCard }
  | {
      card: ApdmBuildCardKind.CouponChoice;
      couponChoice: ApdmPlanCouponChoiceCard;
    }
  | { card: ApdmBuildCardKind.Messages; messages: ApdmPlanMessagesCard }
  | { card: ApdmBuildCardKind.Campaign; campaign: ApdmPlanCampaignCard }
  | { card: ApdmBuildCardKind.Upload; upload: ApdmPlanUploadCard };

export interface ApdmBuildCardResult {
  card: ApdmBuildCardView | null;
}

/**
 * When the first messages go out. A launch always creates and starts the
 * campaign; its dates and daily window decide when it sends.
 */
export enum ApdmPlanFirstSend {
  /** At the first daily window on or after the start date. */
  AsScheduled = 'as_scheduled',
  /**
   * Now, when the start date is today and its window has not opened yet;
   * later days keep the window. Anywhere else it is as scheduled.
   */
  Immediately = 'immediately',
}

export interface ApdmPlanLaunchInput {
  messageId: string;
  firstSend: ApdmPlanFirstSend;
  /** The hours the user set on the card; absent keeps the card's. */
  dailyWindow?: ApdmCampaignDailyWindow;
}

export interface ApdmPlanLaunchResult {
  campaignId: string;
  status: ApdmCampaignStatus;
  startAt: string;
}

/** `apdm_plan_step`: the question of a step that has no card of its own. */
export type ApdmPlanStepWidget =
  | { step: ApdmBuildStep.What; suggested: ApdmCampaignKind | null }
  | {
      step: ApdmBuildStep.When;
      /** `YYYY-MM-DD` */
      startDate: string;
      /** `YYYY-MM-DD`, or null for no end. */
      endDate: string | null;
      /** The planner's proposal; absent proposes the kind's default. */
      dailyCap?: number | null;
      /** The hours the user named; absent proposes the kind's default. */
      dailyWindow?: ApdmCampaignDailyWindow;
    };

/** A calendar date `YYYY-MM-DD` that exists. */
export function isApdmDate(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    Number.isFinite(Date.parse(value + 'T00:00:00Z')) &&
    new Date(value + 'T00:00:00Z').toISOString().startsWith(value)
  );
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export function parseApdmPlanStepWidget(
  value: unknown,
): ApdmPlanStepWidget | null {
  const row = record(value);
  if (row?.step === ApdmBuildStep.What)
    return {
      step: ApdmBuildStep.What,
      suggested: Object.values(ApdmCampaignKind).includes(
        row.suggested as ApdmCampaignKind,
      )
        ? (row.suggested as ApdmCampaignKind)
        : null,
    };
  if (
    row?.step === ApdmBuildStep.When &&
    isApdmDate(row.startDate) &&
    (row.endDate === null || isApdmDate(row.endDate))
  ) {
    const dailyWindow = parseApdmDailyWindow(row.dailyWindow);
    return {
      step: ApdmBuildStep.When,
      startDate: row.startDate as string,
      endDate: (row.endDate as string | null) ?? null,
      ...(row.dailyCap === null ||
      (Number.isSafeInteger(row.dailyCap) && (row.dailyCap as number) >= 1)
        ? { dailyCap: row.dailyCap as number | null }
        : {}),
      ...(dailyWindow ? { dailyWindow } : {}),
    };
  }
  return null;
}

/** `apdm_plan_card`: names a card the reply's turn kept. */
export function parseApdmPlanCardWidget(
  value: unknown,
): ApdmBuildCardKind | null {
  const card = record(value)?.card;
  return Object.values(ApdmBuildCardKind).includes(card as ApdmBuildCardKind)
    ? (card as ApdmBuildCardKind)
    : null;
}
